/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import { ButtonGroup } from '@atlaskit/button';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout';
import { getWillShowNav3 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav3.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { DEFAULT_ITEM_HEIGHT } from '@atlassian/jira-software-deployments-common-table/src/model/table';
import { NonCriticalErrorBoundary } from '../common/utils/non-critical-error-boundary';
import useQueryParamSync from '../controllers/query-param-sync';
import { ISSUE_PAGE_SIZE } from '../services/core-data/constants.tsx';
import { ViewDeploymentsExperienceSuccessTracker } from '../services/experience-tracker';
import { PerformanceTracker } from '../services/performance-tracker';
import ConfigurationButton from './configuration-button';
import DateFilter from './date-filter';
import Header from './header';
import { HEADER_HEIGHT_PX } from './header/constants';
import InsightsButton from './insights-button';
import { Providers } from './providers';
import Sidebar from './sidebar';
import SidebarContent from './sidebar-content';
import Timeline from './timeline';
import TodayButton from './today-button';
import TopFilters from './top-filters';
import { usePreventSwipeBack } from './utils';

type Props = {
	projectKey: ProjectKey;
};

const Main = ({ projectKey }: Props) => {
	usePreventSwipeBack();
	useQueryParamSync();

	return (
		<Providers projectKey={projectKey}>
			<div css={mainDeploymentsWrapperStyles}>
				<Header
					leftContent={<TopFilters />}
					rightContent={
						<ButtonGroup>
							<TodayButton />
							<DateFilter />
							{getWillShowNav4() && <ConfigurationButton />}
							<InsightsButton />
							{getWillShowNav3() && <ConfigurationButton />}
						</ButtonGroup>
					}
				/>
				<div css={bodyWrapperStyles}>
					<div css={tableWrapperStyles}>
						{/* @ts-expect-error - TS2322 - Type '{ projectKey: string; }' is not assignable to type 'IntrinsicAttributes'. */}
						<Timeline projectKey={projectKey} />
					</div>
					<Sidebar>
						<SidebarContent />
					</Sidebar>
				</div>
				<NonCriticalErrorBoundary fallback="unmount" eventLocation="performanceTracker">
					<PerformanceTracker />
				</NonCriticalErrorBoundary>
				<NonCriticalErrorBoundary
					fallback="unmount"
					eventLocation="viewDeploymentsExperienceSuccessTracker"
				>
					<ViewDeploymentsExperienceSuccessTracker />
				</NonCriticalErrorBoundary>
			</div>
		</Providers>
	);
};

const mainDeploymentsWrapperStyles = css({
	padding: `0 ${token('space.500', '40px')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP})`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP})`,
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
});

const bottomPadding = token('space.200', '16px');
// NOTE Spotlight is wrapping BodyWrapper so we must explicity set the height here

// max-height is given for exceptionally long screens. If the table is viewed in a vertically long screen, user can never trigger pagination, because 40 issues are displayed without vertical scroll at all.
// This could be a temporary fix. We might want to revisit this for a proper solution
const bodyWrapperStyles = css({
	display: 'flex',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${HEADER_HEIGHT_PX} - ${bottomPadding})`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: `${DEFAULT_ITEM_HEIGHT * (ISSUE_PAGE_SIZE - 1)}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: `${token('color.background.neutral.subtle', colors.N0)}`,

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	marginBottom: `${bottomPadding} !important` /* important because spotlight applies margin too */,
});

const tableWrapperStyles = css({
	position: 'relative',
	flexGrow: 1,
	overflow: 'hidden',
});

export { Main };
export default Main;
